import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Category, Product } from '@/types'

import Link from 'next/link'
import { Wheat } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getCategories, getProducts } from '@/lib/catalog'
import { buttonVariants } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'
import { ProductCard } from '@/components/products/product-card'
import { CtaSection } from '@/components/landing/cta-section'

interface ProductsPageProps {
    categories: Category[]
    products: Product[]
}

interface CatalogGroup {
    key: string
    anchor: string
    category: Category | null
    products: Product[]
}

const buildGroups = (categories: Category[], products: Product[]): CatalogGroup[] => {
    const knownIds = new Set(categories.map(category => category.id))

    const grouped: CatalogGroup[] = categories.map(category => ({
        key: category.id,
        anchor: category.slug,
        category,
        products: products.filter(product => product.category_id === category.id)
    }))

    grouped.push({
        key: 'other',
        anchor: 'other-rice',
        category: null,
        products: products.filter(product => !product.category_id || !knownIds.has(product.category_id))
    })

    return grouped.filter(group => group.products.length > 0)
}

const ProductsPage: FC<ProductsPageProps> = ({ categories, products }): ReactNode => {
    const groups = buildGroups(categories, products)

    return (
        <Layout>
            <Seo
                title='Rice varieties'
                description='Browse our wholesale rice range: basmati, non-basmati, parboiled and broken rice, with grain length, moisture, broken percentage, packing and minimum order for every variety.'
            />

            <section id='catalog-intro' aria-labelledby='catalog-title' className='border-b border-border bg-secondary/60'>
                <Container className='py-16 sm:py-20 lg:py-24'>
                    <SectionHeading
                        as='h1'
                        id='catalog-title'
                        eyebrow='Rice catalogue'
                        title='Rice varieties we supply'
                        description='Every variety is sortex cleaned and graded to a written specification. Open any grade for its full specification, packing and minimum order. Need something that is not listed? We source other grades on request.'
                    />

                    {groups.length > 1 && (
                        <nav aria-label='Rice categories' className='mt-10'>
                            <ul className='flex flex-wrap gap-2'>
                                {groups.map(group => (
                                    <li key={group.key}>
                                        <a
                                            href={`#${group.anchor}`}
                                            className='inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground shadow-xs transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                                        >
                                            {group.category ? (
                                                <span data-mdlfy-cms={`categories:${group.category.id}:name`}>
                                                    {group.category.name}
                                                </span>
                                            ) : (
                                                <span>
                                                    Other rice
                                                </span>
                                            )}

                                            <span className='rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground'>
                                                {group.products.length}
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                </Container>
            </section>

            {groups.length > 0 ? (
                groups.map(group => (
                    <section
                        key={group.key}
                        id={group.anchor}
                        aria-labelledby={`${group.anchor}-title`}
                        className='border-b border-border py-16 last-of-type:border-b-0 sm:py-20'
                    >
                        <Container>
                            <div className='max-w-3xl'>
                                <h2
                                    id={`${group.anchor}-title`}
                                    className='font-heading text-3xl font-medium tracking-tight text-foreground sm:text-4xl'
                                >
                                    {group.category ? (
                                        <span data-mdlfy-cms={`categories:${group.category.id}:name`}>
                                            {group.category.name}
                                        </span>
                                    ) : (
                                        <span>
                                            Other rice
                                        </span>
                                    )}
                                </h2>

                                {group.category?.description && (
                                    <p
                                        className='mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg'
                                        data-mdlfy-cms={`categories:${group.category.id}:description`}
                                    >
                                        {group.category.description}
                                    </p>
                                )}
                            </div>

                            <ul className='mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                                {group.products.map(product => (
                                    <li key={product.id}>
                                        <ProductCard product={product} />
                                    </li>
                                ))}
                            </ul>
                        </Container>
                    </section>
                ))
            ) : (
                <section id='catalog-empty' aria-labelledby='catalog-empty-title' className='py-20'>
                    <Container>
                        <div className='mx-auto flex max-w-xl flex-col items-center rounded-2xl border border-dashed border-border bg-card px-6 py-14 text-center'>
                            <span className='flex size-12 items-center justify-center rounded-xl bg-secondary text-bronze'>
                                <Wheat className='size-6' aria-hidden='true' />
                            </span>

                            <h2 id='catalog-empty-title' className='mt-5 text-lg font-bold'>
                                Our rice list is being updated
                            </h2>

                            <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                Tell us the variety and quantity you need and we will send current availability, specifications and prices.
                            </p>

                            <Link href='/contact' className={cn(buttonVariants(), 'mt-6')}>
                                Request a quote
                            </Link>
                        </div>
                    </Container>
                </section>
            )}

            <CtaSection />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ProductsPageProps> = async () => {
    try {
        const [categories, products] = await Promise.all([getCategories(), getProducts()])

        return {
            props: { categories, products },
            revalidate: 60
        }
    }
    catch (error) {
        console.error('Rice catalog could not be loaded:', error instanceof Error ? error.message : error)

        return {
            props: { categories: [], products: [] },
            revalidate: 10
        }
    }
}

export default ProductsPage
